import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TechnologyService } from '../../../../core/services/technology.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Technology } from '../../../../core/models/technology';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { labelValidator } from '../../../../core/utils/utils';


@Component({
  selector: 'em-technology-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, InputTextModule, ButtonModule, ToastModule, TranslatePipe],
  templateUrl: './technology-form.component.html',
  styleUrl: './technology-form.component.scss',
  providers: [MessageService],
})



export class TechnologyFormComponent {
  form!: FormGroup;
  formErrors: any;
  isEditMode: boolean = false;
  translations: any = {};
  originalTechnology!: Technology;
  constructor(
    private fb: FormBuilder,
    private technologyService: TechnologyService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
  ) { }


  ngOnInit() {
    this.loadTranslations();

    this.form = this.fb.group({
      id: [null],
      label: [
        null,
        [Validators.required, labelValidator.bind(this)]
      ],
    });

    if (this.config.data) {
      this.isEditMode = true;
      this.originalTechnology = structuredClone(this.config.data);
      this.populateForm(this.config.data);
    }
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.technologies.addTechnology.errorMessage.alreadyExists',
        'layout.configurations.technologies.addTechnology.errorMessage.title',
        'layout.configurations.technologies.editTechnology.errorMessage.title',
        'layout.configurations.technologies.editTechnology.errorMessage.alreadyExists',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  populateForm(technology: Technology) {
    this.form.patchValue({
      id: technology.id,
      label: technology.label,
    });
  }

  onSave() {
    if (this.form.invalid) {
      Object.values(this.form.controls).forEach((control) => {
        control.markAsTouched();
      });
      return;
    }

    if (this.isEditMode) {
      this.updateTechnology();
    } else {
      this.addTechnology();
    }
  }

  addTechnology() {

    this.form.patchValue({
      label: this.form.value.label.trim()
    });
    this.technologyService.addTechnology(this.form.value).subscribe({
      next: (technology: Technology) => {
        this.ref.close(technology);
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.technologies.addTechnology.errorMessage.title'],
          detail: this.translations['layout.configurations.technologies.addTechnology.errorMessage.alreadyExists'],
        });
      },
    });
  }

  updateTechnology() {

    this.form.patchValue({
      label: this.form.value.label.trim()
    });
    const updatedTechnology = this.form.value;
    const comparableOriginal = {
      id: this.originalTechnology.id,
      label: this.originalTechnology.label,
    };

    const isChanged = JSON.stringify(comparableOriginal) !== JSON.stringify(updatedTechnology);
    if (!isChanged) {
      this.ref.close({ type: 'no-change' });
      return;
    }
    this.technologyService.updateTechnology(updatedTechnology).subscribe({
      next: (technology: Technology) => {
        this.ref.close({ type: 'updated', data: technology });
      },
      error: (error) => {
        this.formErrors = error?.error?.fieldErrors;
        console.log('Error while updating the technology: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.technologies.editTechnology.errorMessage.title'],
          detail: this.translations['layout.configurations.technologies.editTechnology.errorMessage.alreadyExists'],
        });
      },
    });
  }

  isInvalid(key: string) {
    const control = this.form.get(key);
    return control?.invalid && (control?.dirty || control?.touched);
  }

  getErrorMessage(field: string) {
    return this.formErrors?.find((obj: { field: string }) => obj.field == field)?.errorMessage || '';
  }
}
